import { useContext, useEffect, useState } from 'react';
import LegoAdminPageContext from '../../legoAdminPageContext';
import useAsyncEffect from '../../../components/common/react-hooks/useAsyncEffect';
import _ from 'lodash';
import { FactoidSpecs } from './FactoidSpecs';
import { SchemaChangeOpsList } from './SchemaChangeOpsList';
import FactoidViewer from './FactoidViewer';
import  { getCleanSchema } from '../spec-schema-editor/SpecSchemaEditor';
import { SwitchInput } from '../../../components/common/SwitchInput';
import { sortObjectKeysDeep } from '../../../../../model/cerokm/cerokmUtils.mjs';

function incrementVersionString(version) {
  let [major, minor, patch] = version.split('.');
  patch = patch || '0';
  return `${major}.${minor}.${parseInt(patch) + 1}`;
}

export default function SaveSchemaChangePreview({ operations, changedSchema, originalSchema, appliedFixes, onSavedChanges, onSaveFired, fixesState }) {
  const { page } = useContext(LegoAdminPageContext);

  const factoidSvc = page.service('services/cerokm/sourcefactoid');

  const appliedFixesFragmentIds = _.uniq(_.flatten(_.map(appliedFixes, fix => fix.sourceFragmentIds)));

  const [impactData, setImpactData] = useState(null);
  const [impactAppliedFixes, setImpactAppliedFixes] = useState(null);
  const [reparseObsolete, setReparseObsolete] = useState(true);
  const [deleteOldFactoids, setDeleteOldFactoids] = useState(true);
  const [reparseGroupingBySpecGroup, setReparseGroupingBySpecGroup] = useState(true);

  useAsyncEffect(async () => {
    const res = await Promise.all(operations.map(async ({ op, path, fromPath, toPath, value }) => {
      // Remove .properties of the schema
      let instancePath = (path || fromPath).replaceAll('.properties.', '.').replace(/^properties\./, '');

      let clearKeysAndItems = s => s.replaceAll(/\bvaluesType\b/g, '[^\\.]+').replaceAll(/\bitems\b/g, '\\d+');

      //TODO: Ver otros types y que pasa si nestean el objeto completo
      const $select = { derivedSpecs: 1, sourceFragmentId: 1 };
      let pathRegex;
      switch (op) {
        case 'add':
        case 'remove':
        case 'move':
          // Any key prefixed with the touched
          pathRegex = { $regex: clearKeysAndItems(`^${_.escapeRegExp(instancePath)}($|\\.)`) };

          console.log(`Checking ${instancePath}`);
          return factoidSvc.find({
            query: {
              'derivedSpecs': { $elemMatch: { '0': pathRegex } }, isObsolete: { $ne: true }, $select
            }
          });
        case 'addItem':
          return [];
        case 'removeItem':
          console.log(`Checking ${instancePath}`);
          if (instancePath.endsWith('.keys')) {
            pathRegex = { $regex: clearKeysAndItems(`^${_.escapeRegExp(instancePath.replace(/\.keys$/, '.' + value))}($|\\.)`) };

            return factoidSvc.find({
              query: {
                'derivedSpecs': { $elemMatch: { '0': pathRegex } }, isObsolete: { $ne: true }, $select
              }
            });
          } else if (instancePath.endsWith('.values')) {
            pathRegex = { $regex: clearKeysAndItems(`^${_.escapeRegExp(instancePath.replace(/\.values$/, ''))}($|\\.)`) };

            console.log(pathRegex);
            return factoidSvc.find({
              query: {
                'derivedSpecs': { $elemMatch: { '0': pathRegex, '1': value } }, isObsolete: { $ne: true }, $select
              }
            });
          } else {
            throw new Error('Remove item is only supported for objectTypedKeys keys or enum values');
          }
        case 'renameItem':
          if (instancePath.endsWith('.values')) {
            pathRegex = { $regex: clearKeysAndItems(`^${instancePath.replace(/\.values$/, '')}($|\\.)`) };

            return factoidSvc.find({
              query: {
                'derivedSpecs': { $elemMatch: { '0': pathRegex, '1': value } }, isObsolete: { $ne: true }, $select
              }
            });
          } else {
            throw new Error('Rename item is only supported for enum values');
          }
        default:
          throw new Error('Operation ${op} is not supported');
      }
    }));

    if(appliedFixesFragmentIds.length) {
      const affected = await factoidSvc.find({ query: { sourceFragmentId: { $in: appliedFixesFragmentIds } , $select: {_id: 1} } });
      setImpactAppliedFixes(affected);
    }

    setImpactData(res);
  }, [operations, originalSchema]);


  function saveChangesToDatabase() {
    page.runAsync(async () => {
      // Increment version, set basedOnVersion and save new doc with changed schema
      let newDoc = {
        ... originalSchema,
        basedOnVersion: originalSchema.version,
        version: incrementVersionString(originalSchema.version),
        createdBy: page.getLoggedUserSignature(),
        schema: sortObjectKeysDeep(getCleanSchema(changedSchema))
      }
      delete newDoc._id;
      delete newDoc.createdAt;
      delete newDoc.updatedAt;

      onSaveFired(newDoc);
      const svcSchemas = page.service('services/cerokm/schema');
      let savedNewDoc = await svcSchemas.create(newDoc);
      onSavedChanges(savedNewDoc);

      let idsToMakeObsolete = _.uniq(_.flatMap(impactData, fs => fs.map(f => f._id)));
      let fragmentsToReparse = _.uniq(_.flatMap(impactData, fs => fs.map(f => f.sourceFragmentId)).concat(appliedFixesFragmentIds));

      if(deleteOldFactoids) {
        let obsoleteRes = await factoidSvc.remove({query: { _id: {$in: idsToMakeObsolete} }});
        console.log('Deleted factoids', obsoleteRes);
      } else {
        let obsoleteRes = await factoidSvc.update({query: { _id: {$in: idsToMakeObsolete} }}, {$set: {isObsolete: true, obsoleteReason: 'Schema Change '+newDoc.version }});
        console.log('Obsolete factoids', obsoleteRes);
      }

      if (appliedFixes.length) {
        let fixesRes = await page.service('services/cerokm/schemafix').update({query: { _id: {$in: appliedFixes.map(f => f._id)}}}, {$set: {state: 'accepted'}});
        console.log('Aplied Fixes', fixesRes);

        if (appliedFixesFragmentIds.length) {
          if(deleteOldFactoids) {
            let res = await factoidSvc.remove({ query: { sourceFragmentId: { $in: appliedFixesFragmentIds } } });
            console.log('Deleted factoids', res);
          } else {
            let res = await factoidSvc.update({ query: { sourceFragmentId: { $in: appliedFixesFragmentIds } } }, { $set: { isObsolete: true, obsoleteReason: 'Schema Change ' + newDoc.version } });
            console.log('Obsoleted fix fragments factoids', res);
          }
        }
      }

      if(reparseObsolete && fragmentsToReparse.length) {
        console.log('Send to reparse', fragmentsToReparse);
        let svcRes = await page.service('services/scripts/runs').create({scriptName: 'ParseFragments', parameters: {
          "limit": fragmentsToReparse.length,
          "fragmentIds": fragmentsToReparse.join(','),
          "parallelism": 5,
          "chunkSize": 20,
          "groupBySpecGroup": reparseGroupingBySpecGroup,
          "onlyWithoutFactoids": false,
          "saveSpecs": true,
          "model": null
        }});
        console.log('Reparse script launched', svcRes);
      }

      let toReject = _.filter(_.toPairs(fixesState), ([id, state]) => state === 'reject');
      if(toReject.length) {
        await page.service('services/cerokm/schemafix').update({query: { _id: {$in: toReject.map(([id,state]) => id)}}}, {$set: {state: 'rejected'}});
      }
    }, 'Saving changes to database...');
  }


  const openFactoidsDetail = factoids => {
    page.openModal(<FactoidViewer ids={_.map(factoids, '_id')}/>, {title: <>Impacted factoids: <span className={'badge badge-primary'}>{factoids.length}</span></>});
  }


  if (impactData) {

    let idsToMakeObsolete = _.uniq(_.flatMap(impactData, fs => fs.map(f => f._id)));
    let fragmentsToReparse = _.uniq(_.flatMap(impactData, fs => fs.map(f => f.sourceFragmentId)).concat(appliedFixesFragmentIds));

    let totalFactoids = idsToMakeObsolete.length;
    let totalFragmentsToReparse = fragmentsToReparse.length;

    return <div className={'p-2'}>
      <table className={'table table-sm bg-light'}>
        <thead className={'bg-dark text-white'}>
        <tr>
          <th></th>
          <th>Operations</th>
          <th className={'text-center'}>Impacted factoids</th>
        </tr>
        </thead>
        <tbody>
        {_.map(impactData, ((factoids, i) => <tr key={i}>
          <td className={'text-secondary small'}>{i + 1}</td>
          <td><SchemaChangeOpsList ops={[operations[i]]}/></td>
          <td className={'text-center'}>
            {
              factoids.length > 0 ?
                <button className={'btn btn-link btn-sm p-0 m-0'}
                        onClick={() => openFactoidsDetail(factoids)}>{factoids.length} factoids</button>
                :
                '-'
            }
          </td>
        </tr>))}

        <tr>
          <td className={'text-secondary small'}></td>

          <td className={'text-center'}>
            Fragments directly involved in applied fixes: {appliedFixesFragmentIds.length}
          </td>

          <td>
            {impactAppliedFixes?.length || '-'}
          </td>
        </tr>

        <tr>
          <td colSpan={3} className={'bg-warning text-center h6'}>Total: {totalFactoids} obsolete factoids, {totalFragmentsToReparse} fragments to reparse</td>
        </tr>
        </tbody>
      </table>

      <div className={'p-2 text-center'}>
        <div className={'mr-2'}>
          <SwitchInput label={'Reparse obsolete'} value={reparseObsolete} onChange={setReparseObsolete} className={'mr-4'}/>
          <SwitchInput label={'Reparse grouping by spec group'} value={reparseObsolete && reparseGroupingBySpecGroup} onChange={setReparseGroupingBySpecGroup} className={'mr-4'}/>
          <SwitchInput label={'Delete old factoids'} value={deleteOldFactoids} onChange={setDeleteOldFactoids}/>
        </div>

        <div className={'mt-3'}>
          <span className={'btn btn-primary'} onClick={saveChangesToDatabase}>Proceed to save changes and mark <span
            className={'badge badge-dark'}>{totalFactoids}</span> factoids obsolete</span>
        </div>

      </div>
    </div>;
  } else {
    return <div className={'p-2'}>Loading...</div>;
  }
}
